import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, EyeOff, Link2, Loader2, RefreshCw, Shuffle, UserX } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};
const span = (dates) => (dates.length === 1 ? fmt(dates[0]) : `${fmt(dates[0])}–${fmt(dates[dates.length - 1])}`);
const ago = (iso) => {
  if (!iso) return 'never read';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? 'read just now' : m < 60 ? `read ${m} min ago` : `read ${Math.round(m / 60)} h ago`;
};
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export const SheetCheck = ({ backendUrl, canEdit, onFixed, refreshKey }) => {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState('');
  const [showNames, setShowNames] = useState(false);
  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${backendUrl}/api/manpower/health`, { headers: headers() });
      setHealth(r.data);
    } catch { /* strip is advisory — never block the schedule */ }
  }, [backendUrl, headers]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);

  const fix = async (key, body, okMsg) => {
    setBusy(key);
    try {
      await axios.post(`${backendUrl}/api/manpower/health/fix`, body, { headers: headers() });
      toast.success(okMsg);
      await load();
      onFixed?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not apply that fix'); }
    finally { setBusy(''); }
  };

  const reread = async () => {
    setBusy('sync');
    try {
      await axios.post(`${backendUrl}/api/manpower/sync`, {}, { headers: headers() });
      await load();
      onFixed?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sheet read failed'); }
    finally { setBusy(''); }
  };

  if (!health) return null;
  const issues = health.issue_count || 0;
  const names = health.unmatched || [];
  const Btn = ({ k, onClick, children }) => (
    <button onClick={onClick} disabled={!!busy}
      className="inline-flex items-center gap-1 rounded-full border border-amber-700/30 bg-white px-2.5 py-0.5 text-[11px] font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-50 transition-colors whitespace-nowrap"
      data-testid={`sheet-check-fix-${k}`}>
      {busy === k ? <Loader2 className="h-3 w-3 animate-spin" /> : null}{children}
    </button>
  );

  return (
    <div className={`px-5 py-1.5 border-b text-[11px] flex-shrink-0 ${issues || health.last_error ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50/60 border-emerald-100'}`}
      data-testid="sheet-check-strip">
      <div className="flex items-center gap-2 flex-wrap">
        {issues || health.last_error ? (
          <span className="flex items-center gap-1 font-bold text-amber-900" data-testid="sheet-check-status">
            <AlertTriangle className="h-3.5 w-3.5" /> Schedule ≠ dispatch sheet — {issues || 1} thing{issues === 1 ? '' : 's'} to fix
          </span>
        ) : (
          <span className="flex items-center gap-1 font-bold text-emerald-800" data-testid="sheet-check-status">
            <CheckCircle2 className="h-3.5 w-3.5" /> Matches the dispatch sheet
          </span>
        )}
        <span className="text-slate-500">· sheet {ago(health.last_sync_at)}{health.headcount ? ` · ${health.headcount} millwrights` : ''}</span>
        {canEdit && (
          <button onClick={reread} disabled={!!busy} className="flex items-center gap-1 text-sky-800 font-semibold hover:underline disabled:opacity-50" data-testid="sheet-check-reread-btn">
            {busy === 'sync' ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Re-read now
          </button>
        )}
        {names.length > 0 && (
          <button onClick={() => setShowNames((v) => !v)} className="flex items-center gap-1 text-slate-600 hover:text-slate-900 ml-auto" data-testid="sheet-check-unmatched-toggle"
            title="Names on the sheet that aren't 365Ops users — they still count in the daily totals and show as 'sheet name' on the bars">
            <UserX className="h-3 w-3" /> {names.length} sheet name{names.length === 1 ? '' : 's'} not in Ops
          </button>
        )}
      </div>
      {health.last_error && <div className="text-red-700 mt-0.5" data-testid="sheet-check-error">Last sheet read failed: {health.last_error}</div>}
      {showNames && <div className="text-slate-600 mt-0.5" data-testid="sheet-check-unmatched-names">{names.join(' · ')} — add them as users (Admin → Users) and the bars pick them up on the next read.</div>}
      {(health.links || []).length > 0 && (
        <div className="mt-0.5 flex items-center gap-x-3 gap-y-0.5 flex-wrap text-slate-600" data-testid="sheet-check-links">
          <span className="flex items-center gap-1"><Link2 className="h-3 w-3" /> WO changes picked up from the sheet:</span>
          {health.links.map((l) => (
            <span key={l.id} className="inline-flex items-center gap-1" data-testid={`sheet-check-link-${l.from_wo}`}>
              <b>{l.from_wo}</b> → <b>{l.to_wo}</b> from {fmt(l.effective_from)}{l.auto ? '' : ` (${l.created_by})`}
              {canEdit && <button onClick={() => { if (window.confirm(`Unlink ${l.from_wo} → ${l.to_wo}? The sheet's ${l.from_wo} days will show under ${l.from_wo} again.`)) fix(`unlink-${l.id}`, { action: 'unlink', id: l.id }, 'Unlinked'); }}
                className="text-sky-800 hover:underline disabled:opacity-50" disabled={!!busy} data-testid={`sheet-check-unlink-${l.from_wo}`}>undo</button>}
            </span>
          ))}
        </div>
      )}
      {issues > 0 && (
        <ul className="mt-1 space-y-0.5" data-testid="sheet-check-issues">
          {health.hidden.map((h) => (
            <li key={`h-${h.sheet_wo}`} className="flex items-center gap-2 flex-wrap text-amber-950" data-testid={`sheet-check-hidden-${h.sheet_wo}`}>
              <EyeOff className="h-3 w-3 text-amber-700 flex-shrink-0" />
              <span><b>{h.sheet_wo}</b> {h.customer} · {h.dates.length} sheet day{h.dates.length === 1 ? '' : 's'} ({span(h.dates)}) hidden here since {h.deleted_by || 'someone'} deleted the bar{h.deleted_at ? ` (${when(h.deleted_at)})` : ''}</span>
              {canEdit && <Btn k={`restore-${h.sheet_wo}`} onClick={() => fix(`restore-${h.sheet_wo}`, { action: 'restore_hidden', sheet_wo: h.sheet_wo }, `${h.sheet_wo} follows the sheet again`)}>Show them again</Btn>}
            </li>
          ))}
          {health.renumber.map((r) => {
            const dayWo = r.plan_wo === r.from_wo ? r.to_wo : r.from_wo;
            const link = (from, to) => fix(`redirect-${from}`, { action: 'redirect', from_wo: from, to_wo: to, effective_from: r.effective_from, customer: r.to_customer }, `${from} now counts as ${to} from ${fmt(r.effective_from)}`);
            return (
              <li key={`r-${r.from_wo}-${r.to_wo}`} className="flex items-center gap-2 flex-wrap text-amber-950" data-testid={`sheet-check-renumber-${r.from_wo}`}>
                <Shuffle className="h-3 w-3 text-amber-700 flex-shrink-0" />
                <span>{r.confidence === 'high' ? 'Monthly WO change: ' : 'Looks like the same job: '}the Monthly View counts {r.plan_customer || r.from_customer || 'it'} under <b>{r.plan_wo}</b> ({r.planned} men) while the day tabs run it as <b>{dayWo}</b> {r.to_customer} from {fmt(r.effective_from)} — it shows twice on {r.days} days</span>
                {canEdit && r.confidence === 'high' && <Btn k={`redirect-${r.from_wo}`} onClick={() => link(r.from_wo, r.to_wo)}>Count {r.from_wo} as {r.to_wo} from {fmt(r.effective_from)}</Btn>}
                {canEdit && r.confidence !== 'high' && (
                  <>
                    <Btn k={`redirect-${r.plan_wo}`} onClick={() => link(r.plan_wo, dayWo)}>Use {dayWo} (day tabs)</Btn>
                    <Btn k={`redirect-${dayWo}`} onClick={() => link(dayWo, r.plan_wo)}>Use {r.plan_wo} (Monthly View)</Btn>
                  </>
                )}
              </li>
            );
          })}
          {health.overrides.map((o) => (
            <li key={`o-${o.work_order}`} className="flex items-center gap-2 flex-wrap text-amber-950" data-testid={`sheet-check-override-${o.work_order}`}>
              <AlertTriangle className="h-3 w-3 text-amber-700 flex-shrink-0" />
              <span><b>{o.work_order}</b> {o.customer} · {span(o.dates)}: an Ops bar ({o.ref}) with a different shift overrides the sheet's names</span>
              {canEdit && <Btn k={`use-sheet-${o.work_order}`} onClick={() => { if (window.confirm(`Remove the Ops bar(s) on ${o.work_order} and show the sheet's crew instead?`)) fix(`use-sheet-${o.work_order}`, { action: 'use_sheet', work_order: o.work_order }, `${o.work_order} now shows the sheet`); }}>Use the sheet</Btn>}
            </li>
          ))}
          {health.no_project.map((n) => (
            <li key={`n-${n.work_order}`} className="flex items-center gap-2 flex-wrap text-amber-950" data-testid={`sheet-check-noproject-${n.work_order}`}>
              <AlertTriangle className="h-3 w-3 text-amber-700 flex-shrink-0" />
              <span><b>{n.work_order}</b> {n.customer} · {span(n.dates)}: on the sheet but no WO with that number in Ops — check the number on the sheet or the WO book</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SheetCheck;
